import { findOwnedConnection } from "@/lib/utils/connections/find-owned-connection";
import { useMatterConflictConfirmation } from "@/components/features/matter/layout/useMatterConflictConfirmation";
import {
  EditContactForm,
  type EditContactFormHandle,
} from "@/components/features/contact/AddOrEditContactModal/EditContactForm";
import {
  ActionConfirmationModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  TextAreaFormField,
  Typography,
} from "@/components/ui";
import {
  AddContactFormField,
  contactRoleSelectPrompt,
} from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionRichCard } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  retrieveMatterQueryOptions,
  useUpdateMatter,
} from "@/lib/data/react-query/matters/matter";
import {
  useDeleteMatterContactConnection,
  useUpdateMatterContactConnection,
} from "@/lib/data/react-query/matters/matter-contact-connection";
import {
  useDeleteConnection,
  useReplaceConnection,
  useUpdateConnection,
} from "@/lib/data/react-query/connections/connection";
import { MatterContactConnectionSchema } from "@/lib/data/schemas/matters/MatterSchema";
import {
  MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS,
  NOT_APPLICABLE_INSURANCE_COVERAGE,
} from "@/lib/data/static/matters/insuranceCoverageStatics";
import {
  MATTER_CLIENT_MARITAL_STATUS_OPTIONS,
  type MATTER_CONTACT_CONNECTION_LABEL_OPTIONS,
  MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Contact, type MatterContactConnection } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_SHELL_COMPACT_CLASS,
} from "@/lib/utils/contact-role-select-card-class";
import { getRemoveMatterContactConnectionModalCopy } from "@/lib/utils/matterContactConnectionHelpers";
import {
  createMatterPartyConnectionInput,
  isSupportedMatterPartyRole,
} from "@/lib/utils/connections/matter-party-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Pencil, PlusCircle } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export interface EditMatterContactConnectionFormProps {
  matterContactConnectionContactId: string;
  matterContactConnection?: MatterContactConnection;
  labelOptions: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
  disableTime?: boolean;
  disableFormSubmit?: boolean;
  disableDelete?: boolean;
  isOpen: boolean;
  matterId: string;
  setIsOpen: (open: boolean) => void;
  setIsDirty: (dirty: boolean) => void;
  /** Contact section mode. `hidden` keeps contact preview in parent modal only. */
  contactEditorMode?: "chip" | "inline" | "hidden";
  /** Hide Cancel / Save row (parent modal provides footer). */
  hideInternalFooter?: boolean;
  /** When false, successful save does not close the shell (combined modal closes explicitly). */
  closeOnSuccessfulSubmit?: boolean;
  overrideEditFn?: (
    connection: Pick<
      MatterContactConnection,
      "dateModified" | "id" | "matterId"
    > &
      Pick<
        MatterContactConnection,
        | "dateTimeEnd"
        | "dateTimeStart"
        | "description"
        | "faultStatus"
        | "insuranceCoverage"
        | "label"
        | "witnessStatus"
      >,
  ) => void;
  overrideDeleteFn?: (
    connection: Pick<MatterContactConnection, "id" | "matterId">,
  ) => void;
}

export type EditMatterContactConnectionFormHandle = {
  /** Save matter-connection fields, then inline contact form when `contactEditorMode` is `inline`. */
  submitCombined: () => Promise<boolean>;
  /** Opens the delete confirmation flow (parent modal footer). */
  requestDelete: () => void;
};

const createFormSchema = (requireTimeFields: boolean) =>
  z
    .object({
      matterContactConnectionContact: z.unknown(),
      clientMaritalStatus: z
        .enum(MATTER_CLIENT_MARITAL_STATUS_OPTIONS)
        .nullable()
        .optional(),
    })
    .merge(
      MatterContactConnectionSchema.pick({
        label: true,
        description: true,
        dateTimeStart: true,
        dateTimeEnd: true,
        faultStatus: true,
        witnessStatus: true,
      }),
    )
    .extend({
      insuranceNotApplicable: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (!requireTimeFields) {
        return true;
      }
      if (data.dateTimeEnd && !data.dateTimeStart) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateTimeStart"],
          message: "Cannot have only an end date",
          fatal: true,
        });
      } else if (!data.dateTimeStart) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateTimeStart"],
          message: "Start date is required",
          fatal: true,
        });
      } else if (
        data.dateTimeEnd &&
        dayjs(data.dateTimeEnd).isBefore(data.dateTimeStart)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateTimeEnd"],
          message: "End must be after start",
          fatal: true,
        });
        return;
      }
      return true;
    });

type EditMatterContactConnectionFormValues = z.infer<
  ReturnType<typeof createFormSchema>
>;

const defaultFormValues = {
  matterContactConnectionContact: {
    id: "",
    individualFirstName: "",
    individualLastName: "",
    type: "individual",
    organizationName: "",
    addresses: [],
    emails: [],
    phones: [],
  },
  description: "",
  dateTimeStart: dayjs().format("YYYY-MM-DD").toString(),
  dateTimeEnd: "",
  faultStatus: undefined,
  witnessStatus: undefined,
  insuranceNotApplicable: false,
  clientMaritalStatus: null,
} as const;

export const EditMatterContactConnectionForm = forwardRef<
  EditMatterContactConnectionFormHandle,
  EditMatterContactConnectionFormProps
>(function EditMatterContactConnectionForm(
  {
    matterContactConnectionContactId,
    matterContactConnection,
    labelOptions,
    disableFormSubmit,
    disableTime,
    disableDelete,
    isOpen,
    matterId,
    setIsDirty,
    setIsOpen,
    contactEditorMode = "chip",
    hideInternalFooter = false,
    closeOnSuccessfulSubmit = true,
    overrideEditFn,
    overrideDeleteFn,
  },
  ref,
) {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const { openMatterContactConnectionModal } = useGlobalModal();
  const { hasActionPermission } = useUser();
  const deleteMatterContactConnection = useDeleteMatterContactConnection();
  const deleteConnection = useDeleteConnection();
  const replaceConnection = useReplaceConnection();
  const updateConnection = useUpdateConnection();
  const updateMatter = useUpdateMatter();
  const liveMatterContactConnection = getMatterContactConnections(matter).find(
    (connection) => connection.id === matterContactConnection?.id,
  );
  const liveGenericConnection = matterContactConnection
    ? findOwnedConnection(matter, matterContactConnection.id)
    : undefined;
  const effectiveMatterContactConnection =
    liveMatterContactConnection ?? matterContactConnection;
  const spouseConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Spouse",
  );
  const form = useForm<EditMatterContactConnectionFormValues>({
    resolver: zodResolver(createFormSchema(!disableTime)),
    defaultValues: {
      ...defaultFormValues,
      label: effectiveMatterContactConnection?.label ?? labelOptions[0],
      clientMaritalStatus:
        matter.clientMaritalStatus === "Single" ||
        matter.clientMaritalStatus === "Married" ||
        matter.clientMaritalStatus === "Divorced" ||
        matter.clientMaritalStatus === "Separated" ||
        matter.clientMaritalStatus === "Widowed"
          ? matter.clientMaritalStatus
          : defaultFormValues.clientMaritalStatus,
      description:
        matterContactConnection?.description ?? defaultFormValues.description,
      dateTimeStart:
        matterContactConnection?.dateTimeStart ??
        defaultFormValues.dateTimeStart,
      dateTimeEnd:
        matterContactConnection?.dateTimeEnd ?? defaultFormValues.dateTimeEnd,
      faultStatus: matterContactConnection?.faultStatus ?? null,
      witnessStatus: matterContactConnection?.witnessStatus ?? null,
      insuranceNotApplicable:
        matterContactConnection?.insuranceCoverage ===
        NOT_APPLICABLE_INSURANCE_COVERAGE,
      matterContactConnectionContact: {
        id:
          effectiveMatterContactConnection?.contact.id ??
          matterContactConnectionContactId,
        individualFirstName:
          effectiveMatterContactConnection?.contact.individualFirstName ?? null,
        individualLastName:
          effectiveMatterContactConnection?.contact.individualLastName ?? null,
        type: effectiveMatterContactConnection?.contact.type ?? "individual",
        organizationName:
          effectiveMatterContactConnection?.contact.organizationName ?? null,
        addresses: effectiveMatterContactConnection?.contact.addresses ?? [],
        emails: effectiveMatterContactConnection?.contact.emails ?? [],
        phones: effectiveMatterContactConnection?.contact.phones ?? [],
      },
    },
    mode: "onSubmit",
  });

  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );

  const [isDeleting, setIsDeleting] = useState(false);
  const removeMatterContactModalCopy =
    getRemoveMatterContactConnectionModalCopy(
      effectiveMatterContactConnection?.label ?? "",
    );

  const updateMatterContactConnection = useUpdateMatterContactConnection();
  const hasUpdateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const selectedLabel = form.watch("label");
  const showInsuranceToggle = selectedLabel === "Client";
  const showFaultStatus = selectedLabel === "Defendant";
  const showWitnessStatus = selectedLabel === "Witness";
  const showMaritalStatus = selectedLabel === "Client";
  const isMarried = form.watch("clientMaritalStatus") === "Married";
  const spouseSlideoverId = `matter-contact-connection-spouse-${matterId}`;

  const contactFormRef = useRef<EditContactFormHandle>(null);
  const replacementIntentRef = useRef<{
    key: string;
    operationId: string;
  } | null>(null);
  const [inlineContactDirty, setInlineContactDirty] = useState(false);
  const useParentScroll =
    contactEditorMode === "inline" || contactEditorMode === "hidden";

  const { confirmParty, conflictModal } = useMatterConflictConfirmation(isOpen);

  const saveConnection = async (
    data: EditMatterContactConnectionFormValues,
    options?: { skipClose?: boolean },
  ) => {
    if (!effectiveMatterContactConnection) {
      return;
    }
    if (
      data.label !== effectiveMatterContactConnection.label &&
      !(await confirmParty({
        contactId: effectiveMatterContactConnection.contactId,
        label: data.label,
        matterId,
      }))
    ) {
      return false;
    }
    if (
      showMaritalStatus &&
      data.clientMaritalStatus !== matter.clientMaritalStatus
    ) {
      await updateMatter.mutateAsync({
        id: matterId,
        dateModified: matter.dateModified,
        clientMaritalStatus: data.clientMaritalStatus,
      });
    }
    const payload = {
      id: effectiveMatterContactConnection.id,
      matterId: matterId,
      label: data.label,
      description: data.description,
      dateTimeStart: data.dateTimeStart ?? "",
      dateTimeEnd: data.dateTimeEnd ?? "",
      insuranceCoverage: data.insuranceNotApplicable
        ? NOT_APPLICABLE_INSURANCE_COVERAGE
        : null,
      faultStatus: data.faultStatus,
      witnessStatus: data.witnessStatus,
      dateModified: effectiveMatterContactConnection.dateModified,
    };
    if (overrideEditFn) {
      overrideEditFn(payload);
    } else if (liveGenericConnection) {
      if (
        liveGenericConnection.sourceObjectType === "matter" &&
        !isSupportedMatterPartyRole(data.label)
      ) {
        throw new Error(`${data.label} is not a supported matter party role.`);
      }
      // A date-only control displays local days. Preserve the exact stored
      // instant when saving another field instead of rounding it to midnight.
      const dateTimeStart = form.getFieldState("dateTimeStart", form.formState)
        .isDirty
        ? data.dateTimeStart
          ? dayjs(data.dateTimeStart).startOf("day").toISOString()
          : liveGenericConnection.dateTimeStart
        : liveGenericConnection.dateTimeStart;
      const dateTimeEnd = form.getFieldState("dateTimeEnd", form.formState)
        .isDirty
        ? data.dateTimeEnd
          ? dayjs(data.dateTimeEnd).startOf("day").toISOString()
          : null
        : liveGenericConnection.dateTimeEnd;
      const connectionInput =
        liveGenericConnection.sourceObjectType === "matter"
          ? createMatterPartyConnectionInput({
              contactId: liveGenericConnection.targetObjectId,
              dateTimeEnd,
              dateTimeStart,
              description: data.description ?? null,
              faultStatus: data.faultStatus ?? null,
              insuranceCoverage: data.insuranceNotApplicable
                ? NOT_APPLICABLE_INSURANCE_COVERAGE
                : null,
              matterId,
              role: data.label as Parameters<
                typeof createMatterPartyConnectionInput
              >[0]["role"],
              witnessStatus: data.witnessStatus ?? null,
            })
          : {
              sourceObjectType: liveGenericConnection.sourceObjectType,
              sourceObjectId: liveGenericConnection.sourceObjectId,
              targetObjectType: "contact",
              targetObjectId: liveGenericConnection.targetObjectId,
              connectionType: `${liveGenericConnection.sourceObjectType}_to_${data.label.toLowerCase().replace(/ /g, "_")}`,
              description: data.description,
              dateTimeStart,
              dateTimeEnd,
              faultStatus: data.faultStatus,
              witnessStatus: data.witnessStatus,
              insuranceCoverage: data.insuranceNotApplicable
                ? NOT_APPLICABLE_INSURANCE_COVERAGE
                : null,
            };
      if (
        connectionInput.connectionType !== liveGenericConnection.connectionType
      ) {
        const replacementIntentKey = JSON.stringify({
          connectionId: liveGenericConnection.id,
          ...connectionInput,
          operationId: undefined,
        });
        if (replacementIntentRef.current?.key !== replacementIntentKey) {
          replacementIntentRef.current = {
            key: replacementIntentKey,
            operationId: crypto.randomUUID(),
          };
        }
        await replaceConnection.mutateAsync({
          connectionId: liveGenericConnection.id,
          dateModified: liveGenericConnection.dateModified,
          matterId,
          operation: "replace",
          replacement: {
            ...connectionInput,
            // A role change creates a new current assignment. Let the backend
            // establish the replacement boundary instead of reusing the
            // predecessor's historical start date.
            dateTimeStart: undefined,
            operationId: replacementIntentRef.current.operationId,
          },
        });
      } else {
        await updateConnection.mutateAsync({
          connectionId: liveGenericConnection.id,
          dateModified: liveGenericConnection.dateModified,
          dateTimeEnd: connectionInput.dateTimeEnd ?? null,
          ...(connectionInput.dateTimeStart
            ? { dateTimeStart: connectionInput.dateTimeStart }
            : {}),
          description: connectionInput.description ?? null,
          faultStatus: connectionInput.faultStatus ?? null,
          insuranceCoverage: connectionInput.insuranceCoverage ?? null,
          matterId,
          operation: "update",
          witnessStatus: connectionInput.witnessStatus ?? null,
        });
      }
      toast.success("Matter party updated successfully");
    } else {
      await updateMatterContactConnection.mutateAsync(payload);
    }
    if (closeOnSuccessfulSubmit && !options?.skipClose) {
      setIsOpen(false);
    }
  };

  const onSubmitForm = async (data: EditMatterContactConnectionFormValues) => {
    await saveConnection(data);
  };

  useImperativeHandle(ref, () => ({
    submitCombined: () =>
      new Promise<boolean>((resolve) => {
        void form.handleSubmit(
          async (data) => {
            try {
              if ((await saveConnection(data, { skipClose: true })) === false) {
                resolve(false);
                return;
              }
              if (contactEditorMode === "inline" && contactFormRef.current) {
                const ok = await contactFormRef.current.submit();
                resolve(ok);
                return;
              }
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          () => {
            resolve(false);
          },
        )();
      }),
    requestDelete: () => {
      setIsDeleting(true);
    },
  }));

  const onDelete = () => {
    setIsOpen(false);
    if (!effectiveMatterContactConnection) {
      return;
    }
    const payload = {
      id: effectiveMatterContactConnection.id,
      matterId: matterId,
    };
    if (overrideDeleteFn) {
      overrideDeleteFn(payload);
    } else if (liveGenericConnection) {
      deleteConnection.mutate({
        connectionId: liveGenericConnection.id,
        matterId,
      });
    } else {
      deleteMatterContactConnection.mutate(payload);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  useEffect(() => {
    if (!showWitnessStatus) {
      form.setValue("witnessStatus", null);
    }
  }, [showWitnessStatus, form]);

  useEffect(() => {
    if (contactEditorMode === "inline") {
      setIsDirty(form.formState.isDirty || inlineContactDirty);
    } else {
      setIsDirty(form.formState.isDirty);
    }
  }, [
    contactEditorMode,
    form.formState.isDirty,
    inlineContactDirty,
    setIsDirty,
  ]);

  useEffect(() => {
    if (contactEditorMode !== "inline") {
      setInlineContactDirty(false);
    }
  }, [contactEditorMode, isOpen]);

  const syncMatterContactChip = (updated: Contact) => {
    form.setValue(
      "matterContactConnectionContact",
      {
        id: updated.id,
        individualFirstName: updated.individualFirstName,
        individualLastName: updated.individualLastName,
        type: updated.type,
        organizationName: updated.organizationName,
        addresses: updated.addresses,
        emails: updated.emails,
        phones: updated.phones,
      },
      { shouldDirty: false },
    );
  };

  return (
    <>
      {contactEditorMode === "inline" ? (
        <div className="border-border mb-6 border-b px-1 pb-6">
          <Typography className="mb-4" weight="semibold" size="sm">
            Contact details
          </Typography>
          <EditContactForm
            ref={contactFormRef}
            open={isOpen}
            setOpen={setIsOpen}
            activeContactId={matterContactConnectionContactId}
            hideFooter
            closeOnSave={false}
            onDirtyChange={setInlineContactDirty}
            hasEditPermission={hasActionPermission(
              ACTION_PERMISSION_KEYS.contact,
              ACTION_PERMISSION_VALUES.update,
            )}
            handleSubmit={syncMatterContactChip}
            fieldsetClassName={modalFormFieldsetClassName}
            scrollContainer="parent"
            matterId={matterId}
          />
        </div>
      ) : null}
      <Form {...form}>
        {conflictModal}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit(onSubmitForm)();
          }}
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            useParentScroll ? "overflow-visible" : "overflow-hidden",
          )}
        >
          <div
            className={cn(
              useParentScroll
                ? "overflow-visible"
                : "min-h-0 flex-1 overflow-y-auto overscroll-contain",
            )}
          >
            <fieldset
              className={slideoverFieldsetClassName}
              disabled={
                !hasActionPermission(
                  ACTION_PERMISSION_KEYS.matter,
                  ACTION_PERMISSION_VALUES.update,
                )
              }
            >
              {contactEditorMode === "chip" ? (
                <div className="col-span-1">
                  <AddContactFormField
                    disableDelete
                    label={effectiveMatterContactConnection?.label}
                    register={form.register("matterContactConnectionContact")}
                    matterId={matterId}
                  />
                </div>
              ) : null}
              {showMaritalStatus && isMarried && (
                <div className="col-span-1">
                  <div className="bg-accent rounded-md p-1">
                    <div className="flex items-center">
                      <Typography weight="medium" size="sm">
                        Spouse
                      </Typography>
                      {hasUpdateMatterContactConnectionPermission &&
                        spouseConnection && (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              openMatterContactConnectionModal({
                                matterContactConnectionContactId:
                                  spouseConnection.contactId,
                                matterContactConnection: spouseConnection,
                                matterId,
                                slideoverId: spouseSlideoverId,
                                labelOptions: ["Spouse"],
                                disableTime: true,
                                editing: true,
                                editPresentation: "modal",
                                title: "Edit spouse information",
                                autoOpenContactSelection: !spouseConnection,
                              });
                            }}
                          >
                            <Pencil className="w-4" />
                          </Button>
                        )}
                    </div>
                    {spouseConnection ? (
                      <MatterContactConnectionRichCard
                        contact={spouseConnection.contact}
                        matterContactConnection={spouseConnection}
                        matterId={matter.id}
                        className="w-full border-0 p-0 shadow-none"
                      />
                    ) : (
                      <div
                        className={
                          !hasCreateMatterContactConnectionPermission
                            ? "pointer-events-none opacity-40"
                            : undefined
                        }
                      >
                        <div
                          className={
                            CONTACT_ROLE_SELECT_EMPTY_SHELL_COMPACT_CLASS
                          }
                        >
                          <button
                            type="button"
                            className={CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS}
                            disabled={
                              !hasCreateMatterContactConnectionPermission
                            }
                            onClick={() => {
                              openMatterContactConnectionModal({
                                matterId,
                                slideoverId: spouseSlideoverId,
                                labelOptions: ["Spouse"],
                                disableTime: true,
                                selectContactOnly: true,
                                createPresentation: "modal",
                                title: "Add spouse information",
                              });
                            }}
                            aria-label={contactRoleSelectPrompt("Spouse")}
                          />
                          <div className={CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS}>
                            <div
                              className={
                                CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS
                              }
                            >
                              <span className="bg-muted text-muted-foreground flex h-7 w-7 items-center justify-center rounded-full">
                                <PlusCircle className="h-3.5 w-3.5 shrink-0" />
                              </span>
                              <Typography className="text-foreground line-clamp-1 text-sm leading-tight font-medium">
                                {contactRoleSelectPrompt("Spouse")}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {labelOptions.length > 1 && (
                <SimpleSelectFormField
                  register={form.register("label")}
                  items={labelOptions}
                  placeholder="Select a label"
                  label="Connection label*"
                />
              )}
              <div className="col-span-2">
                <TextAreaFormField
                  label="Description"
                  register={form.register("description")}
                />
              </div>
              {showMaritalStatus && (
                <SimpleSelectFormField
                  label="Marital status"
                  register={form.register("clientMaritalStatus")}
                  items={MATTER_CLIENT_MARITAL_STATUS_OPTIONS}
                  placeholder="Select marital status"
                />
              )}
              {!disableTime && (
                <>
                  <DateFormField
                    label="Start date"
                    register={form.register("dateTimeStart")}
                    name="dateTimeStart"
                  />
                  <DateFormField
                    label="End date"
                    register={form.register("dateTimeEnd")}
                    name="dateTimeEnd"
                  />
                </>
              )}
              {showInsuranceToggle && (
                <CheckboxFormField
                  register={form.register("insuranceNotApplicable")}
                  label={
                    <Typography size="sm" weight="medium">
                      No insurance
                    </Typography>
                  }
                />
              )}
              {showFaultStatus && (
                <SimpleSelectFormField
                  register={form.register("faultStatus")}
                  items={[...MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS]}
                  placeholder="Select fault status"
                  label="Fault status"
                />
              )}
              {showWitnessStatus && (
                <SimpleSelectFormField
                  register={form.register("witnessStatus")}
                  items={[...MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS]}
                  placeholder="Select witness status"
                  label="Witness status"
                />
              )}
            </fieldset>
          </div>
          <div className="flex-grow" />
          {/* Uncomment this when we are ready to change clients */}
          {!disableFormSubmit && !hideInternalFooter ? (
            <SheetFooter addSlideoverStyles>
              {hasDeletePermission && !disableDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setIsDeleting(true);
                  }}
                >
                  {removeMatterContactModalCopy.deleteButtonText}
                </Button>
              )}

              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  setOpen={setIsOpen}
                  isDirty={
                    contactEditorMode === "inline"
                      ? form.formState.isDirty || inlineContactDirty
                      : form.formState.isDirty
                  }
                />
                <FormSubmitButton />
              </div>
            </SheetFooter>
          ) : null}
        </form>
        <ActionConfirmationModal
          title={removeMatterContactModalCopy.title}
          description={removeMatterContactModalCopy.description}
          buttonText={removeMatterContactModalCopy.buttonText}
          open={isDeleting}
          setOpen={setIsDeleting}
          afterActionFn={() => {
            onDelete();
          }}
        />
      </Form>
    </>
  );
});
